import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getUserId = () => {
  let uid = localStorage.getItem('watchlist_user_id');
  if (!uid) {
    uid = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('watchlist_user_id', uid);
  }
  return uid;
};

export const useWatchlistStore = create(
  persist(
    (set, get) => ({
      watchlist: [],
      insights: [],
      loaded: false,

      loadWatchlist: async () => {
        try {
          const { data } = await axios.get(`${API}/watchlist/${getUserId()}`);
          if (data.status === 'success') {
            const wl = data.watchlist || [];
            set({ watchlist: wl, loaded: true });
            if (wl.length > 0) get().fetchInsights(wl);
          }
        } catch {
          set({ loaded: true });
        }
      },

      insightsLoading: false,

      fetchInsights: async (wl) => {
        const list = wl || get().watchlist;
        const symbols = list.map(i => i.symbol).filter(Boolean).join(',');
        if (!symbols) return;
        set({ insightsLoading: true });
        try {
          const { data } = await axios.get(`${API}/india/watchlist-insights?symbols=${symbols}&days=30`);
          const live = (data.insights || []).map((ins, idx) => ({
            ...ins,
            id: `live_${idx}_${ins.symbol}_${ins.date || ''}`,
            isRead: false,
            watchlistItemId: null,
          }));
          set(s => ({ insights: [...live, ...s.insights.filter(i => !i.id?.startsWith('live_'))], insightsLoading: false }));
        } catch {
          set({ insightsLoading: false });
        }
      },

      addToWatchlist: async (itemData) => {
        const userId = getUserId();
        const payload = {
          user_id: userId,
          symbol: itemData.symbol,
          company_name: itemData.companyName || itemData.company_name || '',
          exchange: itemData.exchange || 'NSE',
          company_id: itemData.isin || '',
        };

        set((state) => ({
          watchlist: [...state.watchlist, {
            ...payload,
            id: `temp_${Date.now()}`,
            added_at: new Date().toISOString(),
          }],
        }));

        try {
          const { data } = await axios.post(`${API}/watchlist`, payload);
          if (data.status === 'success' && data.item) {
            set((state) => ({
              watchlist: state.watchlist.map((w) =>
                w.id?.startsWith('temp_') && w.symbol === payload.symbol ? data.item : w
              ),
            }));
          }
        } catch (err) {
          console.error('Failed to save to watchlist:', err);
        }
      },

      removeFromWatchlist: async (id) => {
        set((state) => ({
          watchlist: state.watchlist.filter((item) => item.id !== id),
          insights: state.insights.filter((insight) => insight.watchlistItemId !== id),
        }));
        try {
          await axios.delete(`${API}/watchlist/${id}`);
        } catch (err) {
          console.error('Failed to remove from watchlist:', err);
        }
      },

      isInWatchlist: (symbol) => {
        return get().watchlist.some((item) => item.symbol === symbol);
      },

      getWatchlistItem: (symbol) => {
        return get().watchlist.find((item) => item.symbol === symbol);
      },

      toggleWatchlist: async (itemData) => {
        const sym = itemData.symbol;
        if (get().isInWatchlist(sym)) {
          const item = get().getWatchlistItem(sym);
          if (item) await get().removeFromWatchlist(item.id);
        } else {
          await get().addToWatchlist(itemData);
        }
      },

      addInsight: (insightData) => {
        const newInsight = {
          ...insightData,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({ insights: [newInsight, ...state.insights] }));
      },

      markInsightAsRead: (id) => {
        set((state) => ({
          insights: state.insights.map((insight) =>
            insight.id === id ? { ...insight, isRead: true } : insight
          ),
        }));
      },

      getUnreadInsights: () => get().insights.filter((i) => !i.isRead),

      getInsightsForStock: (symbol) => get().insights.filter((i) => i.symbol === symbol),

      digest: null,
      digestLoading: false,
      digestError: null,

      fetchLatestDigest: async (userId) => {
        const uid = userId || getUserId();
        set({ digestLoading: true, digestError: null });
        try {
          const { data } = await axios.get(`${API}/digest/${uid}/latest`);
          set({ digest: data, digestLoading: false });
        } catch (err) {
          set({ digestLoading: false, digestError: err.response?.status === 404 ? null : 'Failed to load digest', digest: null });
        }
      },

      generateDigest: async (userId) => {
        const uid = userId || getUserId();
        set({ digestLoading: true, digestError: null });
        try {
          const { data } = await axios.post(`${API}/digest/generate`, { user_id: uid });
          if (data.status === 'success') {
            set({ digest: { digest: data.digest, markdown: data.markdown, generated_at: new Date().toISOString() }, digestLoading: false });
          } else {
            set({ digestLoading: false, digestError: data.error || 'Generation failed' });
          }
        } catch (err) {
          set({ digestLoading: false, digestError: err.message || 'Failed to generate digest' });
        }
      },

      clearWatchlist: () => set({ watchlist: [], insights: [], digest: null }),
    }),
    {
      name: 'watchlist-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
